/**
 * The remote support link (Fernwartung): a BLP support operator whose
 * request the installation's admin approved gets one link,
 * `https://<host>/#support=<request id>.<secret>`. Like a mailed login link it
 * is a credential — it signs in once — so it lives in the fragment (never in a
 * server log), is taken out of the address before anything else runs and is
 * never stored or logged: it waits in memory for the sign-in route, which
 * redeems it (`loginWithSupportLink`, `auth.ts`).
 *
 * Only the **fragment** counts. A `support` parameter in the query of the page
 * was in the request line and the access logs before any script ran: it is
 * neither read nor redeemed.
 */

/** A support link: the request it belongs to and its secret. */
export interface SupportLink {
  id: string;
  secret: string;
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
// The operator's 32-byte secret: 64 hex digits or 43 characters of unpadded
// base64url (what master accepts, `support.decodeSecret`).
const SECRET = '(?:[0-9a-fA-F]{64}|[A-Za-z0-9_-]{43})';
const FRAGMENT = new RegExp(`^#support=(${UUID})\\.(${SECRET})$`);

let pending: SupportLink | null = null;
const arrivals = new Set<() => void>();

/**
 * Takes a `#support=…` fragment out of the address with `history.replaceState`
 * (so Back does not bring it back) and keeps a well-formed link for the
 * sign-in route. A fragment that is no link is dropped as well, without a
 * trace of what it held. Every other fragment is left alone. Called before the
 * router module exists (`supportLinkBoot.ts`: the router would read the
 * fragment as a path and could carry it into a `redirect` parameter) and again
 * by the intercept below whenever a link is pasted into a tab that is open.
 */
export function takeSupportLinkFromAddress(): SupportLink | null {
  if (typeof window === 'undefined') return null;
  const { hash = '', pathname = '/', search = '' } = window.location;
  if (!hash.startsWith('#support=')) return null;
  const match = FRAGMENT.exec(hash);
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
  pending = match ? { id: match[1]!.toLowerCase(), secret: match[2]! } : null;
  return pending;
}

/** Calls back when a link arrived in an open tab (see {@link installSupportLinkIntercept}). */
export function onSupportLinkArrived(callback: () => void): () => void {
  arrivals.add(callback);
  return () => {
    arrivals.delete(callback);
  };
}

let uninstall: (() => void) | null = null;

/**
 * Pasting a support link into a tab that already shows the same page changes
 * only the fragment: no page load, so the start-up scrub never runs, and the
 * router would see `#support=<id>.<secret>` as a path (its warnings, a
 * `redirect`). This listens first — it is installed before the router module is
 * loaded — and on `popstate` and `hashchange` takes the link out of the address
 * at once, keeps it for the sign-in view and stops the event for every later
 * listener, so the router never sees it. Idempotent; returns the removal.
 */
export function installSupportLinkIntercept(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  if (!uninstall) {
    const intercept = (event: Event) => {
      if (!window.location.hash.startsWith('#support=')) return;
      event.stopImmediatePropagation();
      if (takeSupportLinkFromAddress()) for (const callback of [...arrivals]) callback();
    };
    window.addEventListener('popstate', intercept);
    window.addEventListener('hashchange', intercept);
    uninstall = () => {
      window.removeEventListener('popstate', intercept);
      window.removeEventListener('hashchange', intercept);
      uninstall = null;
    };
  }
  return () => uninstall?.();
}

/** Whether a link waits for the sign-in route. */
export function hasPendingSupportLink(): boolean {
  return pending !== null;
}

/** Hands the waiting link over, once. */
export function takePendingSupportLink(): SupportLink | null {
  const link = pending;
  pending = null;
  return link;
}

/** Test seam: forget a waiting link. */
export function resetPendingSupportLink(): void {
  pending = null;
}
